import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, List as F7List, ListItem as F7ListItem, f7, f7ready } from 'framework7-react';
import {
  FC,
  Fragment,
  ReactElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spinner } from 'react-bootstrap';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { GagglLogo } from '@assets/GagglLogo';
import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { groupBy, isEmpty, keyBy, orderBy, sortBy } from 'lodash';
import pluralize from 'pluralize';
import {
  AREAS_OF_IMPROVEMENT_ENABLED,
  NEXT_LEVEL_ENTERTAINER_MANAGEMENT_ENABLED,
  RANKINGS_NAVIGATION_ENABLED,
  RATINGS_ENABLED,
  REVIEWS_ENABLED,
} from 'src/constants/FeatureFlags';

import API from '@api/API';

import { CritiqueQuestion } from '@queries/CritiqueQueries';
import {
  Entertainer,
  EntertainerFeaturedCta,
  EntertainerLink,
  EntertainerType,
  ProfileLink,
  TYPES_RECORD,
  UnauthorizedPublicEntertainer,
  useCurrentEntertainerQuery,
  useCurrentUserEntertainerCritiqueQuery,
  useEntertainerByBookingHandleQuery,
  useEntertainerQuickPicksQuery,
  useEntertainerReviewsQuery,
  useRelatedEntertainersByBookingHandleQuery,
  useTopEntertainersQuery,
} from '@queries/EntertainerQueries';
import { FeedEvent, useCurrentUserEventRsvpsQuery } from '@queries/EventQueries';
import { useUserDashboardNotificationsQuery, useUserInterestsQuery } from '@queries/UserQueries';

import {
  useAnswerCritiqueQuestionMutation,
  useCritiqueDocumentMutation,
} from '@mutations/CritiqueMutations';
import {
  useRemoveEntertainerPrimaryCtaMutation,
  useUpdateEntertainerMutation,
} from '@mutations/EntertainerMutations';

import { useTipEntertainer } from '@hooks/EntertainerHooks/useTipEntertainer';
import { useAnalytics } from '@hooks/useAnalytics';
import { useDailyLocalStorageReminderIsVisible } from '@hooks/useDailyLocalStorageReminderIsVisible';
import { useDevice } from '@hooks/useDevice';
import { useExportComponentToImage } from '@hooks/useExportComponentToImage';
import { useShareLink } from '@hooks/useShareLink';

import { useNavigate, useParams, useQueryParams, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import EventCard, { EventHeader, EventImage } from '@components/cards/EventCard';
import { PostCard } from '@components/cards/PostCard/PostCard';
import { ChipRadioOptions } from '@components/chips/ChipRadioOptions';
import { Input } from '@components/form/Input';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { DangerBanner } from '@components/layout/DangerBanner/DangerBanner';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { Popup } from '@components/layout/Popup/Popup';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { PrimaryCritiqueButtons } from '@components/overlays/CritiqueOverlay/CritiqueOverlay';
import { ProfileHeader } from '@components/profile/ProfileHeader/ProfileHeader';
import { ProfileLinksSection } from '@components/profile/ProfileSocialLinksSection/ProfileSocialLinksSection';

import fireIconUrl from '@screens/CommunityScreen/assets/fire.svg';
import { ShowsList } from '@screens/CommunityScreen/components/ShowsList';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { useInAppNotifications } from '@screens/NotificationsScreen/NotificationsScreen';
import { QuickPickQuestionOptions } from '@screens/QuickPicksScreen/QuickPicksScreen';
import { EntertainerAvailabilityCard } from '@screens/booking/GigsAndTalentScreen/EntertainerAvailabilityCard';
import { SuggestedConnectionsBannerLink } from '@screens/connnections/ConnectionsScreen/components/SuggestedConnectionsBannerLink';
import { useShowsGroupedBySectionTitle } from '@screens/event/EventRsvpScreen/hooks/useShowsGroupedBySectionTitle';
import magazineSvgUrl from '@screens/series/SeriesScreen//magazine.svg';
import { SeriesFeatureApplicationSection } from '@screens/series/SeriesScreen/SeriesScreen';
import { SweepstakesCard } from '@screens/series/SeriesScreen/SweepstakesCard';
import dollarsSvgUrl from '@screens/series/SeriesScreen/dollars.svg';

import { VenmoLogo } from '../../../assets/VenmoLogo';
import addSvgUrl from '../../series/SeriesScreen/add.svg';
import {
  CreateEditEntertainerProfileForm,
  DRAG_PERFORMER_CATEGORIES,
} from '../CreateEntertainerProfileScreen';
import { EntertainerScheduleDateDetail } from '../DragKingCommunityScreen/ScheduledSocialPost';
import { CritiqueList } from '../EntertainerRankingsScreen/EntertainerCritiquePopup';
import { FeaturedLookPopup } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import { MultiSelectField } from '../components/CitiesField';
import { VenmoCard } from '../components/VenmoCard';
import { EditEntertainerPopup } from './EditEntertainerPopup';
import { EntertainerCompetitionCard } from './EntertainerCompetitionCard';
import { EntertainerDetailsTabContent } from './EntertainerDetailsTabContent';
import { EntertainerMediaCard } from './EntertainerMediaCard';
import { EntertainerMediaTabContent } from './EntertainerMediaTabContent';
import { EntertainerReviewsContent } from './EntertainerReviews';
import { MetricAction, MetricActionProps } from './MetricAction';
import { PersonalizedBadgeSection } from './PersonalizedBadgeSection';
import { FeaturedKing } from './assets/FeaturedKing';
import calendarIconUrl from './assets/calendar.svg';
import inclusiveResistImage from './assets/inclusive-resist.png';
import prideSvgUrl from './assets/pride.svg';
import appIconUrl from './assets/user-interface.svg';
import userIconUrl from './assets/user.svg';
import { AddEntertainerPrimaryCtaLinkPopup } from './components/AddEntertainerPrimaryCtaLinkPopup';
import { AddEntertainerSocialLinkPopup } from './components/AddEntertainerSocialLinkPopup';
import { AddEventExternalLinkPopup } from './components/AddEventExternalLinkPopup';
import { sendEntertainerDetailsPageView } from './sendEntertainerDetailsPageView';

dayjs.extend(isoWeek);

const RemoveButton = ({ onRemove }: { onRemove: () => Promise<void> }) => {
  return (
    <div style={{ position: 'absolute', top: -4, right: -4 }}>
      <IconButton
        color="neutral"
        onClick={onRemove}
        style={{ width: 14, height: 14, fontSize: 10 }}
      >
        <FontAwesomeIcon name="times" />
      </IconButton>
    </div>
  );
};

const ClaimEntertainerSection: FC<{
  entertainer: UnauthorizedPublicEntertainer;
  onClaim: () => void;
}> = ({ entertainer, onClaim }) => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { bookingHandle } = useParams<{ bookingHandle: string }>();
  const { track } = useAnalytics();

  const { didCopy, shareLink } = useShareLink();
  const handleNotMe = async () => {
    track('Click Not Me');
    await shareLink({ url: `https://werqwith.me/${bookingHandle}` });
  };

  if (currentEntertainer) {
    return (
      <EmptyState
        title="Share Their Profile"
        text={`A Gaggl profile has not been set up for ${entertainer.title}. If you know them, click the button to copy and share their profile link with them so they can get some love!`}
        button={{
          text: didCopy ? 'Copied!' : 'Copy Link',
          onClick: handleNotMe,
        }}
      />
    );
  }

  return (
    <EmptyState
      title="Claim This Profile"
      text={`A Gaggl profile has not been set up for ${entertainer.title}. If this is you, click the button below to set up your free page for reviews, tips, and bookings. If this is not you but you know them, share their profile link with them so they can get some love!`}
      button={{
        text: 'Yes, this is me',
        onClick: onClaim,
      }}
      secondaryButton={{
        text: didCopy ? 'Link Copied!' : 'No, but I know them!',
        onClick: handleNotMe,
      }}
    />
  );
};

export const EntertainerOnboardingLinkButton: FC<{ back: string }> = ({ back }) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { createAuthGateHandler } = useAuthGate();

  const handleEntertainerOnboard = async () => {
    track('Click entertainer onboarding');
    createAuthGateHandler(async () => {
      navigate(`/entertainer/onboarding`, {
        state: {
          back,
        },
      });
    });
  };

  return (
    <LinkBanner
      icon={
        <IconButton className="mx-auto" badge variant="smooth" color="neutral" size="lg">
          <FontAwesomeIcon name="envelope" />
        </IconButton>
      }
      title="Are you a drag artist?"
      description="Create your own free landing page for reviews, tips, and bookings!"
      onClick={handleEntertainerOnboard}
    />
  );
};

const EntertainerLinksSection: FC<{
  entertainer: UnauthorizedPublicEntertainer;
}> = ({ entertainer }) => {
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { updateEntertainerAsync } = useUpdateEntertainerMutation();
  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;

  const entertainerLinks = useMemo<(EntertainerLink & { key?: string })[]>(() => {
    const links = [
      entertainer?.contact?.venmo && {
        type: 'venmo',
        text: 'Venmo',
        url: `venmo://users/${entertainer.contact.venmo}`,
      },
      entertainer?.contact?.instagram && {
        type: 'instagram',
        text: 'Instagram',
        url: `https://instagram.com/${entertainer?.contact?.instagram}`,
      },
      entertainer?.contact?.tiktok && {
        type: 'tiktok',
        text: 'TikTok',
        url: `https://tiktok.com/@${entertainer.contact.tiktok}`,
      },
      ...(entertainer?.contact?.links ?? []).map((link, i) => ({
        ...link,
        key: `custom-link-${i}`,
      })),
      entertainer?.contact?.email && {
        type: 'email',
        text: 'Email',
        url: `mailto:${entertainer?.contact?.email}`,
      },
      entertainer?.contact?.website && {
        type: 'website',
        text: 'Website',
        url: entertainer?.contact?.website,
      },
    ];

    return links.filter(Boolean) as (EntertainerLink & { key?: string })[];
  }, [entertainer]);

  const handleSubmitUpdatedLinks = async (links: ProfileLink[]) => {
    try {
      const updatedLinks = [...(currentEntertainer?.contact?.links ?? []), ...links];

      await updateEntertainerAsync({ contact: { links: updatedLinks } });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const handleRemoveLink = async (link: ProfileLink) => {
    const updatedLinks = currentEntertainer?.contact?.links?.filter(
      ({ url }: any) => url !== link.url,
    );

    await updateEntertainerAsync({ contact: { links: updatedLinks } });
  };

  return (
    <ProfileLinksSection
      canAddLinks={isCurrentEntertainer}
      links={entertainerLinks}
      onSubmitUpdatedLinks={handleSubmitUpdatedLinks}
      onRemoveLink={handleRemoveLink}
    />
  );
};

const QuickPickCard: FC<{
  documentId: string;
  firstQuestionId: string;
  critiqueQuestion: CritiqueQuestion;
  werqwithme: boolean;
  submitCallback?: () => void;
  initCallback?: () => void;
}> = ({
  documentId,
  firstQuestionId,
  critiqueQuestion,
  werqwithme,
  submitCallback,
  initCallback,
}) => {
  const didAlert = useRef(false);
  const hasAnswered = !!critiqueQuestion?.answer && firstQuestionId === critiqueQuestion._id;
  const [value, setValue] = useState<string | undefined>(critiqueQuestion?.answer);
  const { createAuthGateHandler } = useAuthGate();
  const { track } = useAnalytics();
  const { openAlertDialog } = useDialog();

  useLayoutEffect(() => {
    if (hasAnswered && !didAlert.current) {
      didAlert.current = true;
      openAlertDialog({
        title: 'You already answered this question',
        text: 'You can skip to the next question to answer more!',
      });
    } else {
      initCallback?.();
    }
  }, []);

  const { answerCritiqueQuestionAsync } = useAnswerCritiqueQuestionMutation();
  const handleSubmit = () => {
    if (hasAnswered) {
      return submitCallback?.();
    }

    if (value) {
      track('Submit Quick Pick Answer', {
        critiqueQuestionId: critiqueQuestion._id,
        answer: value,
      });

      createAuthGateHandler(async () => {
        await answerCritiqueQuestionAsync({
          critiqueQuestionId: critiqueQuestion._id,
          documentId,
          answer: value,
          meta: { werqwithme },
        });

        submitCallback?.();
      });
    }
  };

  return (
    <Card>
      <Card.Body style={{ borderBottom: '1px solid rgba(255,255,255,.2)' }}>
        <div style={{ fontSize: 16 }}>
          <strong>{critiqueQuestion.question}</strong>
        </div>
      </Card.Body>
      <div className="my-2">
        <QuickPickQuestionOptions
          showSelected
          currentCritiqueQuestion={critiqueQuestion}
          value={value}
          handleSelectOption={setValue}
          onSubmit={handleSubmit}
          submitText={hasAnswered ? 'Next' : 'Submit'}
        />
      </div>
    </Card>
  );
};

export const QuickPicksSection: FC<{
  entertainer: UnauthorizedPublicEntertainer;
  werqwithme: boolean;
  firstQuestionId: string;
}> = ({ entertainer, werqwithme, firstQuestionId }) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { critiqueQuestionsAreReady, critiqueQuestions } = useEntertainerQuickPicksQuery(
    entertainer._id,
    firstQuestionId,
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const titleToolbarRef = useRef<HTMLDivElement>(null);
  const didInitScroll = useRef(false);
  const initCallback = () => {
    if (firstQuestionId && !didInitScroll.current) {
      didInitScroll.current = true;
      setTimeout(() => {
        const targetPosition = titleToolbarRef.current?.offsetTop; // Get the top position of the target element relative to the container
        titleToolbarRef.current?.closest('.page-content')?.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }, 300);
    }
  };

  const currentCritiqueQuestion = critiqueQuestions?.[currentIndex];
  const hasNextQuestion = critiqueQuestions?.[currentIndex + 1];

  return (
    <>
      <TitleToolbar
        divRef={titleToolbarRef}
        text="Hot takes 🔥"
        size="md"
        action={
          hasNextQuestion && (
            <Button
              variant="flat"
              endIcon={<FontAwesomeIcon name="chevron-right" />}
              onClick={() => setCurrentIndex((prev) => prev + 1)}
            >
              Skip Question
            </Button>
          )
        }
      />
      {critiqueQuestionsAreReady ? (
        <>
          {currentCritiqueQuestion ? (
            <QuickPickCard
              documentId={entertainer._id}
              firstQuestionId={firstQuestionId}
              critiqueQuestion={currentCritiqueQuestion}
              werqwithme={werqwithme}
              submitCallback={() => setCurrentIndex((prev) => prev + 1)}
              initCallback={initCallback}
            />
          ) : (
            <EmptyState
              title="No Questions"
              text="You have already answered all the questions for this entertainer. Go to the live feed to see questions for other entertainers!"
              button={{
                text: 'See Live Feed',
                onClick: () => {
                  track('Click See Live Feed');
                  navigate('/');
                },
              }}
            />
          )}
        </>
      ) : (
        <>
          <Skeleton height={240} />
        </>
      )}
    </>
  );
};

const CategoryItem: FC<{ category: string }> = ({ category }) => {
  const { track } = useAnalytics();

  return (
    <Button
      variant="smooth"
      color="neutral"
      disabled={false}
      onClick={() =>
        track('Click Entertainer Category', {
          category,
        })
      }
      style={{
        fontSize: '.7rem',
        borderRadius: 4,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 12,
        paddingRight: 12,
      }}
    >
      {category}
    </Button>
  );
};

const AddCategoryButton = ({
  categories,
  onUpdateCategories,
  defaultOpen,
}: {
  categories: string[];
  onUpdateCategories: (categories: string[]) => void;
  defaultOpen?: boolean;
}) => {
  const { track } = useAnalytics();

  return (
    <MultiSelectField
      options={DRAG_PERFORMER_CATEGORIES.map((value) => ({ value, label: value }))}
      defaultValue={categories}
      onChange={onUpdateCategories}
      defaultOpen={defaultOpen}
      triggerNode={
        <Button
          variant="outlined dashed"
          color="neutral"
          disabled={false}
          startIcon={<FontAwesomeIcon name="plus" />}
          onClick={() => track('Click Add Category')}
          style={{
            fontSize: '.7rem',
            borderRadius: 4,
            paddingTop: 6,
            paddingBottom: 6,
            paddingLeft: 12,
            paddingRight: 12,
          }}
        >
          {categories.length ? 'Update categories' : 'Add Category'}
        </Button>
      }
    />
  );
};

const useInitCategoryAction = (action: string | undefined) => {
  const didInit = useRef(false);
  const [defaultOpen, setDefaultOpen] = useState(false);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      if (action === 'manageCategories') {
        f7ready((f7) => {
          try {
            if (f7.view.current.router.currentRoute.path === '/manage/entertainer') {
              setDefaultOpen(true);
              didInit.current = true;
            }
          } catch (error) {
            console.error(error);
          }
        });
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return { defaultOpen };
};

const CategoriesSection: FC<{
  mt?: string;
  mb?: string;
  entertainer: UnauthorizedPublicEntertainer;
  action?: string;
}> = ({ mt, mb, entertainer, action }) => {
  const { track } = useAnalytics();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const [categories, setCategories] = useState<string[]>(entertainer?.details?.categories ?? []);
  const { updateEntertainerAsync, isSubmitting } = useUpdateEntertainerMutation();
  const { defaultOpen: defaultOpenManageCategories } = useInitCategoryAction(action);
  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;

  const didUpdateCategories = useMemo(() => {
    const sortedCategories = categories.sort((a, b) => a.localeCompare(b));
    const sortedEntertainerCategories = entertainer?.details?.categories?.sort((a, b) =>
      a.localeCompare(b),
    );

    return sortedCategories.join(',') !== sortedEntertainerCategories?.join(',');
  }, [categories, entertainer?.details?.categories]);

  const entertainerCategoryRows = useMemo(() => {
    return categories
      .concat(isCurrentEntertainer ? ['addCategory'] : [])
      ?.sort((a, b) => a.localeCompare(b))
      ?.reduce<Record<number, { items: string[]; charLength: number }>>(
        (rows, interest) => {
          const rowIndex = rows[1].charLength > rows[2].charLength ? 2 : 1;

          rows[rowIndex].items.push(interest);
          rows[rowIndex].charLength += interest.length;

          return rows;
        },
        {
          1: {
            items: [],
            charLength: 0,
          },
          2: {
            items: [],
            charLength: 0,
          },
        },
      );
  }, [isCurrentEntertainer, categories]);

  const handleUpdateCategories = async () => {
    track('Update Entertainer Categories', {
      categories,
    });
    await updateEntertainerAsync({
      categories,
    });
  };

  return (
    <>
      {mt && <div className={mt} />}

      <HorizontalScroll
        style={{ flex: '0 0 auto', width: 'calc(100% + 32px)' }}
        items={[
          <div className="d-flex flex-column gap-1">
            {Object.values(entertainerCategoryRows).map((row, i) => (
              <div key={i} className="d-flex gap-1">
                {row.items.map((category) => (
                  <div key={category} className="interests-section_btn-wrapper">
                    {category === 'addCategory' ? (
                      <AddCategoryButton
                        categories={categories}
                        onUpdateCategories={setCategories}
                        defaultOpen={defaultOpenManageCategories}
                      />
                    ) : (
                      <CategoryItem category={category} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>,
        ]}
      />

      {didUpdateCategories && (
        <div className="mt-2">
          <Button
            color="danger"
            variant="smooth"
            roundness="rounded"
            disabled={isSubmitting}
            onClick={handleUpdateCategories}
          >
            {isSubmitting ? 'Saving...' : 'Save categories'}
          </Button>
        </div>
      )}

      {mb && <div className={mb} />}
    </>
  );
};

const PRIMARY_CTA_CARD_WIDTH = 36;

const PrimaryCtaSection: FC<{ bookingHandle: string }> = ({ bookingHandle }) => {
  const { track } = useAnalytics();
  const { isNativePlatform } = useDevice();
  const { eventRsvpsAreReady, eventRsvps = {} } = useCurrentUserEventRsvpsQuery();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { removeEntertainerPrimaryCtaAsync } = useRemoveEntertainerPrimaryCtaMutation();
  const [
    addEntertainerPrimaryCtaLinkPopupIsVisible,
    setAddEntertainerPrimaryCtaLinkPopupIsVisible,
  ] = useState(false);
  const {
    entertainerIsReady,
    entertainer,
    upcomingEntertainerEvents = [],
    activeEntertainerCompetitions,
    personalizedBadge,
  } = useEntertainerByBookingHandleQuery(bookingHandle);

  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;

  const { openAlertDialog, openConfirmDialog } = useDialog();
  const handleHypeEvents = () => {
    openAlertDialog({
      title: 'Hype Events!',
      text: 'Tap the fire emoji (🔥) next to the events you want to hype',
    });
  };

  const navigate = useNavigate();
  const handleCreateEvent = () => {
    if (isCurrentEntertainer) {
      navigate('/manage/entertainer/events/create');
    }
  };

  const userHypeIndicatorCount = useMemo(() => {
    const rsvps = upcomingEntertainerEvents
      .map((event) => eventRsvps?.[event._id]?.eventInterest)
      .filter(Boolean);

    return upcomingEntertainerEvents.length - rsvps.length;
  }, [eventRsvps, upcomingEntertainerEvents]);

  const showHypeSection = !!userHypeIndicatorCount && !!upcomingEntertainerEvents?.length;

  const isDenverEntertainer = false; // entertainer?.cities?.some(({ name }) => name === 'Denver');

  const handleRemoveCta = async (cta: EntertainerFeaturedCta, index: number) => {
    openConfirmDialog({
      title: `Remove "${cta.title}"?`,
      text: 'Are you sure you want to remove this link?',
      onConfirm: async () => {
        track('Remove Featured CTA', cta);

        await removeEntertainerPrimaryCtaAsync({
          index,
          cta,
        });
      },
      buttonOk: 'Remove',
    });
  };

  if (!isCurrentEntertainer && !entertainer?.featuredCtas?.length) {
    return <></>;
  }

  return (
    <div className="d-grid w-100 gap-2 mt-4">
      {/* {isCurrentEntertainer && <SuggestedConnectionsBannerLink className="my-0" />} */}
      {entertainer?.featuredCtas?.map((cta, index) => (
        <div
          key={`${cta.url}-${cta.img}-${cta.title}-${cta.description}`}
          style={{ position: 'relative' }}
        >
          {isCurrentEntertainer && <RemoveButton onRemove={() => handleRemoveCta(cta, index)} />}
          <PrimaryCtaCard cta={cta} track={{ event: 'Click Entertainer Primary CTA' }} />
        </div>
      ))}
      {isCurrentEntertainer && (
        <>
          {NEXT_LEVEL_ENTERTAINER_MANAGEMENT_ENABLED && (
            <PrimaryCtaCard
              variant="dashed"
              cta={{
                img: addSvgUrl,
                title: 'Add custom link',
                description: `Add your own link here.`,
                url: '',
                onClick: () => {
                  setAddEntertainerPrimaryCtaLinkPopupIsVisible(true);
                },
              }}
              track={{
                event: 'Add custom link',
              }}
            />
          )}
        </>
      )}
      <AddEntertainerPrimaryCtaLinkPopup
        isVisible={addEntertainerPrimaryCtaLinkPopupIsVisible}
        onClose={() => setAddEntertainerPrimaryCtaLinkPopupIsVisible(false)}
      />
    </div>
  );
};
interface EntertainerDetailsScreenProps {
  back?: string;
  preview?: boolean;
  bookingHandle?: string;
  mode?: 'manage';
}

interface EntertainerDetailsScreenQueryParams {
  firstQuestionId?: string;
  mediaId?: string;
  referrer?: string;
  werqwithme_redirect?: string;
  tab?: string;
  receivedBadgesPopup?: string;
  action?: 'manageCategories';
}

const useWerqWithMeLinkBannerIsVisible = () => {
  const { isDailyLocalStorageReminderVisible, handleDailyLocalStorageReminderAcknowledged } =
    useDailyLocalStorageReminderIsVisible('WERQWITHME_LINK_BANNER', 1000, 'isoWeek');

  return {
    isWerqWithMeLinkBannerVisible: isDailyLocalStorageReminderVisible,
    handleWerqWithMeLinkBannerAcknowledged: handleDailyLocalStorageReminderAcknowledged,
  };
};

const WerqWithMeLink = ({
  isCurrentEntertainer,
  bookingHandle: initialBookingHandle,
}: {
  isCurrentEntertainer: boolean;
  bookingHandle: string;
}) => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();
  const [isEditing, setIsEditing] = useState(false);
  const [bookingHandle, setBookingHandle] = useState(initialBookingHandle);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isSubmitting: isUpdatingEntertainer, updateEntertainerAsync } =
    useUpdateEntertainerMutation({ invalidateQueriesOnSuccess: false });

  const bookingLinkRoot = 'werqwith.me/';
  const handleShareClick = async () => {
    if (isEditing) {
      track('Save booking handle');
      await updateEntertainerAsync({
        bookingHandle,
      });
      setIsEditing(false);
    }

    track('Click share entertainer');
    await shareLink({
      url: `https://werqwith.me/${bookingHandle}`,
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const editButtonText = useMemo(() => {
    if (isUpdatingEntertainer) {
      return 'Saving...';
    }

    if (didCopy) {
      return 'Copied!';
    }

    return 'Save';
  }, [isUpdatingEntertainer, didCopy]);

  if (isEditing) {
    return (
      <div
        className="d-flex align-items-center justify-content-between ps-2"
        style={{
          border: '1px solid rgba(255,255,255,.2)',
          borderRadius: 8,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div>
            <FontAwesomeIcon name="link" />
          </div>

          <div className="d-flex align-items-center">
            <span>{bookingLinkRoot}</span>
            <Input
              ref={inputRef}
              className="ps-0 py-1"
              wrapperClassName="flex-grow-1"
              defaultValue={bookingHandle}
              onChange={(e) => setBookingHandle(e.target.value)}
              style={{ height: 24, fontSize: 14, background: 'transparent' }}
            />
          </div>
        </div>
        <div>
          <Button
            variant="flat"
            roundness="rounded"
            size="sm"
            color="neutral"
            className="py-2 px-2 opacity-90"
            onClick={handleShareClick}
          >
            {editButtonText}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2">
        {isCurrentEntertainer ? (
          <div onClick={handleEditClick}>
            <FontAwesomeIcon name="edit" faStyle="far" />
          </div>
        ) : (
          <div>
            <FontAwesomeIcon name="link" />
          </div>
        )}

        <div>
          {bookingLinkRoot}
          {bookingHandle}
        </div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Share'}
        </Button>
      </div>
    </div>
  );
};

export const EntertainerDetailsScreen = createScreenComponent<
  EntertainerDetailsScreenProps,
  EntertainerDetailsScreenQueryParams
>((props) => {
  const { bookingHandle, back: initialBack, preview: isPreviewMode, f7route, mode } = props;
  const { mediaId: initialMediaId, referrer, action } = f7route?.query ?? {};

  const { isNativePlatform } = useDevice();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const {
    entertainerIsReady,
    entertainer,
    upcomingEntertainerEvents,
    upcomingEntertainerScheduleDateDetails = [],
    sharedEntertainerEvents,
    hypedEntertainerEvents,
    activeEntertainerCompetitions,
    personalizedBadge,
    refetchEntertainer,
  } = useEntertainerByBookingHandleQuery(bookingHandle);
  const { reviews } = useEntertainerReviewsQuery(entertainer?._id, {
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { userIsLoggedIn } = useAppContext();
  const { createAuthGateHandler } = useAuthGate();
  const [hideImage, setHideImage] = useState(false);
  const [isEditPopupVisible, setIsEditPopupVisible] = useState(false);
  const { handleVenmoEntertainer, openTipEntertainerPopup, tipEntertainerPopup } =
    useTipEntertainer();
  const [isBadgePopupVisible, setIsBadgePopupVisible] = useState(!!initialMediaId);
  const { openAlertDialog } = useDialog();
  const isWerqWithMeRedirect = f7route?.query?.werqwithme_redirect === 'true';
  const [externalLinkShow, setExternalLinkShow] = useState<
    FeedEvent | EntertainerScheduleDateDetail | undefined
  >();
  const { isWerqWithMeLinkBannerVisible, handleWerqWithMeLinkBannerAcknowledged } =
    useWerqWithMeLinkBannerIsVisible();

  const handleTip = () => {
    handleVenmoEntertainer({
      entertainerId: entertainer._id,
      venmo: entertainer?.contact?.venmo,
    });
  };

  const handleBook = () => {
    navigate(`/noauth/book/${bookingHandle}`, {
      state: {
        back: `/noauth/entertainer/${bookingHandle}`,
      },
    });
  };

  const handleClaim = () => {
    track('Click Claim Entertainer', { entertainerId: entertainer._id });
    createAuthGateHandler(async () => {
      if (entertainer.contact?.instagram) {
        const { entertainer: fetchedEntertainer } = await API.get<{ entertainer: Entertainer }>(
          `/v1/entertainer/instagram/${entertainer.contact?.instagram}`,
        );
        navigate(`/entertainer/onboarding`, {
          state: { entertainer: fetchedEntertainer },
        });
      }
    });
  };

  const didShowPreviewAlert = useRef(false);
  useEffect(() => {
    if (isPreviewMode && !didShowPreviewAlert.current) {
      didShowPreviewAlert.current = true;
      openAlertDialog({
        title: 'Preview Mode',
        text: 'This is a preview of your "werqwith.me" link. Share this link with your fans on social media give them an easy way to rate you and get tips and bookings!',
      });
    }
  }, [isPreviewMode]);

  const didRedirectToCurrentEvent = useRef(false);
  useEffect(() => {
    const currentEvent = upcomingEntertainerEvents?.find(({ isHappeningNow }) => isHappeningNow);

    if (currentEvent && !didRedirectToCurrentEvent.current) {
      didRedirectToCurrentEvent.current = true;
      navigate(`/events/${currentEvent._id}/reviews`);
    }
  }, [upcomingEntertainerEvents]);

  useEffect(() => {
    if (!isPreviewMode && entertainer && entertainer._id) {
      sendEntertainerDetailsPageView(entertainer._id);
    }
  }, [entertainer]);

  const { isClaimed } = entertainer ?? {};
  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;
  const { refetchUserDashboardNotifications } = useUserDashboardNotificationsQuery(
    {
      cityId: entertainer?.cities?.[0]?._id,
    },
    { isEnabled: userIsLoggedIn && isCurrentEntertainer },
  );

  const handleCreateEvent = () => {
    if (isCurrentEntertainer) {
      navigate('/manage/entertainer/schedule');
    }
  };

  const [primaryCity] = entertainer?.cities ?? [];

  const badgeShareMedia = useMemo(() => {
    return entertainer?.featuredMedia?.find(({ _id }) => _id === initialMediaId);
  }, [entertainer, initialMediaId]);

  const performanceMetrics: { count?: number; label: string; to: string }[] = [
    { count: sharedEntertainerEvents?.length, label: 'Shared', to: '#' },
    { count: upcomingEntertainerEvents?.length, label: 'Upcoming', to: '#' },
    { count: hypedEntertainerEvents?.length, label: 'Hyped', to: '#' },
  ];

  const [selectedTab, setSelectedTab] = useState<string>(f7route.query?.tab ?? 'events');
  const tabOptions = [
    { value: 'events', label: 'Shows' },
    // { value: 'details', label: 'Details' },
    { value: 'media', label: 'Media' },
    {
      value: 'reviews',
      label: 'Support',
      badgeCount: reviews?.filter(({ viewedAt }) => !viewedAt)?.length,
    },
  ];
  const back = useMemo(() => {
    const rootUrl =
      mode === 'manage' ? '/manage/entertainer' : `/noauth/entertainer/${bookingHandle}`;
    const params = new URLSearchParams(
      Object.entries(f7route?.query ?? {}).map(([key, value]) => [key, value?.toString()]),
    );

    params.set('tab', selectedTab);

    return `${rootUrl}?${params.toString()}`;
  }, [selectedTab]);

  const { hasShows, hasUpcomingShows, showsGroupedBySectionTitle } = useShowsGroupedBySectionTitle({
    events: sharedEntertainerEvents ?? [],
    scheduleDateDetails: upcomingEntertainerScheduleDateDetails,
    isCurrentEntertainer,
  });

  const handleAddExternalLink = (show: FeedEvent | EntertainerScheduleDateDetail) => {
    setExternalLinkShow(show);
  };

  const showCategoriesSection =
    entertainer?.details?.categories?.length ||
    (isCurrentEntertainer && entertainer?.type === 'dragPerformer');

  return (
    <Screen
      name="Entertainer Details Screen"
      disableLocationPermissionGate
      headerProps={{
        title: <Screen.GagglTitle />,
        left: mode === 'manage' ? <></> : undefined,
        back: initialBack ? { to: initialBack } : undefined,
        right: userIsLoggedIn ? (
          <>
            {/* {isCurrentEntertainer && (
              <Button
                roundness="rounded"
                variant="smooth"
                color="neutral"
                onClick={() => {
                  track('Manage Profile');
                  navigate('/manage/entertainer');
                }}
              >
                Manage Profile
              </Button>
            )} */}
            {isCurrentEntertainer && !isNativePlatform() && (
              <IconButton
                color="neutral"
                variant="smooth"
                badge={!isNativePlatform()}
                onClick={() => {
                  navigate('/notifications');
                }}
              >
                <FontAwesomeIcon name="bell" />
              </IconButton>
            )}
          </>
        ) : (
          <PopoverWrapper
            PopoverContent={({ onClose }) => (
              <div>
                <Button
                  className="w-100"
                  variant="flat"
                  color="neutral"
                  onClick={() => {
                    track('Entertainer Details Log In');
                    onClose();
                    createAuthGateHandler(() => {});
                  }}
                >
                  Log In
                </Button>
              </div>
            )}
          >
            <IconButton variant="smooth" color="neutral">
              <FontAwesomeIcon name="ellipsis-h" className="fs-6" />
            </IconButton>
          </PopoverWrapper>
        ),
      }}
      metaData={{
        title: `${entertainer?.title} — Drag Performer in ${primaryCity?.name}`,
        description: `Check out ${entertainer?.title}! Rate, reviews, tip and book them for your next event!`,
      }}
      hideToolbar={mode !== 'manage'}
      bottomToolbar={
        entertainer?.isPublic &&
        mode !== 'manage' && (
          <Screen.BottomToolbar maxWidth={425}>
            <div className="d-flex gap-2">
              {entertainer?.contact?.venmo && (
                <Button
                  roundness="rounded"
                  startIcon={
                    <div style={{ width: 18, height: 18 }}>
                      <VenmoLogo />
                    </div>
                  }
                  onClick={handleTip}
                >
                  Send a Tip
                </Button>
              )}
              {isClaimed && (
                <>
                  {/* <Button onClick={() => openTipEntertainerPopup(entertainer)}>Tip</Button> */}
                  <Button
                    roundness="rounded"
                    variant="outlined"
                    color="neutral"
                    onClick={handleBook}
                  >
                    Book
                  </Button>
                </>
              )}
            </div>
          </Screen.BottomToolbar>
        )
      }
    >
      <Screen.Content maxWidth={425}>
        {entertainerIsReady ? (
          <>
            {entertainer ? (
              <>
                <div style={{}}>
                  {bookingHandle && (
                    <div className="mb-3">
                      {isCurrentEntertainer &&
                        isWerqWithMeLinkBannerVisible &&
                        !currentEntertainer?.linkCopiedAt && (
                          <div className="mb-2">
                            <DangerBanner
                              title={`This is your "werqwith.me" link!`}
                              text={
                                <div className="d-grid gap-1 mt-1">
                                  <p className="my-0">
                                    {`You know `}
                                    <strong>linktree</strong>
                                    {`, right? This is like that but for drag performers. Edit your profile to update your tagline and use the `}
                                    <strong>"Add Link"</strong>
                                    {` button to update your links! `}
                                  </p>
                                  <p className="my-0">
                                    {`Click the `}
                                    <strong>"Share"</strong>
                                    {` button to copy your link!`}
                                  </p>
                                </div>
                              }
                              button={{
                                text: 'Got it, thanks!',
                                onClick: handleWerqWithMeLinkBannerAcknowledged,
                              }}
                            />
                          </div>
                        )}
                      <WerqWithMeLink
                        isCurrentEntertainer={isCurrentEntertainer}
                        bookingHandle={bookingHandle}
                      />
                    </div>
                  )}
                </div>

                {/* {isCurrentEntertainer && !isNativePlatform() && (
                  <PrimaryCtaCard
                    cta={{
                      img: appIconUrl,
                      title: 'Download the Gaggl App',
                      description: `Confirm your spot in the lineup with the Gaggl app!`,
                      url: 'https://gaggl.app/download',
                      badgeCount: 1,
                    }}
                    track={{ event: 'Click Entertainer Primary CTA' }}
                  />
                )} */}

                <ProfileHeader
                  avatarUrl={entertainer?.avatar ?? entertainer?.featuredMedia?.[0]?.image}
                  title={entertainer?.title ?? ''}
                  isClaimed={isClaimed ?? false}
                  handleClaim={handleClaim}
                  performanceMetrics={performanceMetrics}
                />

                {!!entertainer.tagline && (
                  <div className="mt-1">
                    {entertainer.tagline.split('\n').map((paragraph, index) => (
                      <p key={index} className="m-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}

                {showCategoriesSection && (
                  <CategoriesSection
                    mt="mt-2"
                    mb={showCategoriesSection ? 'mb-2' : ''}
                    entertainer={entertainer}
                    action={action}
                  />
                )}

                <EntertainerLinksSection entertainer={entertainer} />

                {isClaimed
                  ? isCurrentEntertainer && (
                      <div className="mt-3 d-grid gap-2">
                        <VenmoCard entertainer={entertainer} />
                        <Button
                          className="py-2"
                          variant="smooth"
                          color="neutral"
                          roundness="rounded"
                          onClick={() => {
                            setIsEditPopupVisible(true);
                          }}
                        >
                          <span style={{ fontSize: '.75rem' }}>Edit Profile</span>
                        </Button>
                      </div>
                    )
                  : !isWerqWithMeRedirect && (
                      <div className="my-3">
                        <ClaimEntertainerSection entertainer={entertainer} onClaim={handleClaim} />
                      </div>
                    )}

                {personalizedBadge && (
                  <PersonalizedBadgeSection
                    entertainer={entertainer}
                    personalizedBadge={personalizedBadge}
                  />
                )}

                <PrimaryCtaSection bookingHandle={bookingHandle} />

                <ButtonFilter
                  className="mt-4"
                  selected={selectedTab}
                  options={tabOptions}
                  onChange={setSelectedTab}
                />

                {selectedTab === 'events' && (
                  <>
                    {!hasUpcomingShows &&
                      (isCurrentEntertainer ? (
                        <EmptyState
                          title="No upcoming shows"
                          text="You aren't sharing any upcoming shows. Add a show to help the community support you."
                          button={{
                            color: 'primary',
                            roundness: 'rounded',
                            text: 'Manage my public calendar',
                            onClick: handleCreateEvent,
                            badge: 1,
                          }}
                        />
                      ) : (
                        <EmptyState
                          title="No upcoming shows"
                          text={`${entertainer.title} has not listed any upcoming shows.`}
                        />
                      ))}

                    {hasShows && (
                      <ShowsList
                        className="mt-4"
                        hideEntertainerMenuButton
                        showsGroupedBySectionTitle={showsGroupedBySectionTitle}
                        cityId={entertainer?.cities?.[0]?._id}
                        back={back}
                        onAddExternalLink={isCurrentEntertainer ? handleAddExternalLink : undefined}
                      />
                    )}
                  </>
                )}

                {selectedTab === 'details' && <EntertainerDetailsTabContent {...props} />}
                {selectedTab === 'media' && <EntertainerMediaTabContent {...props} />}
                {selectedTab === 'reviews' && (
                  <EntertainerReviewsContent
                    entertainerId={entertainer._id}
                    entertainerTitle={entertainer.title}
                    isPreviewMode={isPreviewMode}
                    referrer={referrer}
                    receivedBadgesPopup={f7route?.query?.receivedBadgesPopup === 'true'}
                  />
                )}
              </>
            ) : (
              <>
                <Skeleton height={240} />
                <Skeleton height={80} />
                <Skeleton height={24} />
              </>
            )}

            {tipEntertainerPopup}
            {isCurrentEntertainer && (
              <EditEntertainerPopup
                isVisible={isEditPopupVisible}
                onClose={() => {
                  setIsEditPopupVisible(false);
                }}
              />
            )}
            {initialMediaId && badgeShareMedia && (
              <FeaturedLookPopup
                isVisible={isBadgePopupVisible}
                onClose={() => {
                  setIsBadgePopupVisible(false);
                }}
                entertainer={entertainer}
                entertainerMedia={badgeShareMedia}
              />
            )}
            {isCurrentEntertainer && (
              <AddEventExternalLinkPopup
                isVisible={!!externalLinkShow}
                externalLinkShow={externalLinkShow}
                onClose={async () => {
                  setExternalLinkShow(undefined);
                  await refetchEntertainer();
                }}
              />
            )}
          </>
        ) : (
          <>
            {/* <EmptyState title="Entertainer not found" text="We could not find this entertainer." /> */}
            <Skeleton height={240} />
            <Skeleton height={80} />
            <Skeleton height={24} />
          </>
        )}
      </Screen.Content>
    </Screen>
  );
});

export const ManageEntertainerProfileDetailsScreen = (props) => {
  const { entertainer } = useCurrentEntertainerQuery();

  return (
    <EntertainerDetailsScreen {...props} bookingHandle={entertainer?.bookingHandle} mode="manage" />
  );
};
